import { Viewer } from '../../Viewer';
import { ViewerLayers, ViewerLayersBit } from '../../Enums/ViewerLayers';
import { ViewerScene } from '../../ViewerScene';
import * as THREE from 'three';
import { ViewerTool } from '../ViewerBaseTool';
import { SVGOverlay } from '../../Overlays/SVGOverlay';
import { ArrowHelper, MathUtils } from 'three';
import { Overlay } from '../../Overlays/Overlay';
import { SVGLabel } from '../../Overlays/SVGObjects';
import { faceInfo, Picker, SnapGeometry } from '../Picker';
import { BaseMeasurement } from './BaseMeasurement';
import { DistanceMeasurement } from './DistanceMeasurement';
import { AngleMeasurement } from './AngleMeasurement';

export enum MeasurementMode {
    DISTANCE,
    ANGLE,
}

export class MeasureTool extends ViewerTool {
    get _SVGOverlay(): SVGOverlay {
        return this._viewer.SVGOverlay;
    }
    get _Overlay(): Overlay {
        return this._viewer.Overlay;
    }

    private _picker: Picker;

    private _activeMeasurement: BaseMeasurement | null = null;
    public get hasActiveMeasurement() {
        return this._activeMeasurement != null;
    }
    public get hasLabels() {
        return this._labels?.length > 0;
    }
    private _measurementStep: number;

    readonly _necessarySteps: Map<MeasurementMode, number> = new Map<MeasurementMode, number>([
        [MeasurementMode.DISTANCE, 2],
        [MeasurementMode.ANGLE, 2],
    ]);
    private measurements: Array<BaseMeasurement>;

    public get hasMeasurements() {
        return this.measurements?.length > 0;
    }

    public get Measurements() {
        return this.measurements;
    }

    public get hasObjects() {
        return this.hasLabels || this.hasMeasurements || this.hasActiveMeasurement;
    }

    private _labels: Array<SVGLabel>;
    private _measurementMode: MeasurementMode = MeasurementMode.DISTANCE;
    public get MeasurementMode() {
        return this._measurementMode;
    }

    /**
     *description
     */
    private _snappingDistance: number = 500;
    /**
     *description
     */
    public get snappingDistance(): number {
        return this._snappingDistance;
    }
    /**
     *description
     */
    public set snappingDistance(v: number) {
        this._snappingDistance = v;
    }

    // Straight lines implementation
    private _straightLines: boolean = false;
    private _startNormal: THREE.Vector3 | null = null;
    private _startPerpendicular: THREE.Vector3 | null = null;

    private _cursorLabel: SVGLabel | null = null;

    private _startPos: THREE.Vector3 | null = null;
    private _startFace: faceInfo | null = null;

    private _snapMode: SnapGeometry = SnapGeometry.VERTEXEDGEFACE;
    private _currentSnapMode: SnapGeometry | null = null;

    _normalMatrix: THREE.Matrix3;
    _worldNormal: THREE.Vector3;

    constructor(viewer: Viewer) {
        super(viewer);
        this.Tool = 'Measure';
        this._picker = this._viewer._picker;

        this._measurementMode = MeasurementMode.DISTANCE;
        this.measurements = [];
        this._measurementStep = 0;
        this._labels = new Array<SVGLabel>();

        this._normalMatrix = new THREE.Matrix3(); // create once and reuse
        this._worldNormal = new THREE.Vector3(); // create once and reuse

        this._cursor = new ArrowHelper(new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 0), 500);
        this._cursor.layers.set(ViewerLayers.INFO);
        this._cursor.visible = false;
    }

    Activate() {
        this._cursor.visible = true;
        let self = this;
        this._viewer._container.addEventListener('blur', (e) => {
            self.CancelActiveMeasurement();
        });
    }

    Deactivate() {
        this._cursor.visible = false;
        this.CancelActiveMeasurement();
        this.ClearMeasurements();
        let self = this;
        this._viewer._container.removeEventListener('blur', (e) => {
            self.CancelActiveMeasurement();
        });
        this._viewer.requestRenderFrame('MeasureTool.Deactivate()');
    }

    cleanUp() {
        this.ClearMeasurements();
        this._viewer.requestRenderFrame('MeasureTool.cleanUp()');
    }

    SetScene(scene: ViewerScene) {
        this.ClearMeasurements();
        if (this._viewer.currentScene) this._viewer.currentScene.remove(this._cursor);
        scene.add(this._cursor);
    }

    OnPointerMove(coords: THREE.Vector2, type: string): void {
        this.FollowCursor(coords);
    }
    OnPrimaryUp(coords: THREE.Vector2, e?: any) {
        this._SVGOverlay.clearMenu();
        // Get current normal and target
        let request = this._picker.requestRaycast(coords, {
            layer: ViewerLayersBit.MODELS,
            snapGeometry: this._snapMode,
            snappingTreshold: this.snappingDistance,
        });

        if (request) this._measurementStep++;
        else return;

        if (this._viewer.currentControls) this._viewer.currentControls.enabled = false;

        if (this._measurementStep === 1) {
            this.StartMeasurement(request);
        }
        // Last end
        else {
            this.CompleteMeasurement(request);
        }
        if (this._viewer.currentControls) this._viewer.currentControls.enabled = true;
        this._viewer.requestRenderFrame('MeasureTool.OnPrimaryUp');
    }

    /**
     * Starts up the measurement with the first intersect
     * @param request
     */
    StartMeasurement(request: any) {
        this._cursorLabel = this._SVGOverlay.createLabel(12, '');
        if (!this._viewer.currentScene) return;
        // Get current normal and target
        if (this._snapMode === SnapGeometry.NONE) {
            this._startNormal = request.normal.clone();
            var normalMatrix = new THREE.Matrix3().getNormalMatrix(request.object.matrixWorld);
            this._startNormal?.applyMatrix3(normalMatrix).normalize();
        } else {
            this._startNormal = request.normal;
        }

        if (!this._startNormal) return;

        this._currentSnapMode = request.mode;
        switch (this._measurementMode) {
            case MeasurementMode.DISTANCE: {
                this._startPos = request.point;
                let upVector =
                    Math.abs(this._startNormal.dot(new THREE.Vector3(0, 1, 0))) > 0.999
                        ? new THREE.Vector3(1, 0, 0)
                        : new THREE.Vector3(0, 1, 0);
                this._startPerpendicular = this._startNormal.clone().cross(upVector);
                let measurement = new DistanceMeasurement(
                    this._viewer.formatting,
                    this._startPos!,
                    this._startPos!,
                    this._startNormal,
                    this._startNormal
                );
                this._viewer.currentScene.add(measurement._mainMeasurement);
                this._activeMeasurement = measurement;
                if(this._startPos)
                this._SVGOverlay.createOverlaySymbol('circle',this._startPos.clone(), "green");
                break;
            }
            case MeasurementMode.ANGLE: {
                this._startFace = request.face;
                let upVector =
                    Math.abs(this._startNormal.dot(new THREE.Vector3(0, 1, 0))) > 0.999
                        ? new THREE.Vector3(1, 0, 0)
                        : new THREE.Vector3(0, 1, 0);
                this._startPerpendicular = this._startNormal.clone().cross(upVector);
                // Check if we have a face to work with
                let face = request.face as faceInfo;
                if (!face) return;

                // Start and end point
                this._startPos = face.center;
                let measurement = new AngleMeasurement(
                    this._viewer.formatting,
                    this._startPos,
                    this._startPos,
                    this._startNormal,
                    this._startNormal
                );
                this._viewer.currentScene.add(measurement._mainMeasurement);
                this._activeMeasurement = measurement;
                if(this._startPos)
                this._SVGOverlay.createOverlaySymbol('circle',this._startPos.clone(), "green");
                break;
            }
        }
    }

    

    /**
     * Completes the measurement with an intersect.
     * @param request
     */
    CompleteMeasurement(request: any) {
        this.SetEndPoint(request);
        this._labels = this._labels.concat(this._activeMeasurement!.addLabels(this._SVGOverlay));

        if (this._cursorLabel) {
            this._SVGOverlay.remove(this._cursorLabel.Object);
            this._cursorLabel = null;
        }
        if (this._toolEvent) this._toolEvent.emit('measurement', { mode: MeasurementMode.DISTANCE, value: this._activeMeasurement?.value });
        this._measurementStep = 0;
        this._activeMeasurement = null;
        this._currentSnapMode = null;
    }

    SetMeasureMode(mode: MeasurementMode) {
        if (this._measurementMode === mode) return;
        this._measurementMode = mode;
        this.CancelActiveMeasurement();
        switch (mode) {
            case MeasurementMode.DISTANCE:
                this._snapMode = SnapGeometry.VERTEXEDGEFACE;
                break;
            case MeasurementMode.ANGLE:
                this._snapMode = SnapGeometry.FACE;
                break;
        }
    }

    CancelActiveMeasurement() {
        // Get rid of the active measurement
        if (this._activeMeasurement instanceof THREE.Object3D) {
            this._viewer.SceneManager.RemoveFromActiveScene(this._activeMeasurement);
            this._activeMeasurement = null;
        } else if (this._activeMeasurement?._mainMeasurement) {
            this._viewer.SceneManager.RemoveFromActiveScene(this._activeMeasurement._mainMeasurement);
            this._activeMeasurement = null;
        }
        if (this._cursorLabel) {
            this._SVGOverlay.remove(this._cursorLabel.Object);
            this._cursorLabel = null;
        }
        this._measurementStep = 0;
        this._currentSnapMode = null;
    }

    onSecondaryDown(coords: THREE.Vector2) {
        // Cancel active measurement if active
        if (this._measurementStep > 0) {
            this.CancelActiveMeasurement();
        }
    }
    OnKeyPressed(keycode: string): void {
        switch (keycode) {
            case 'ShiftLeft':
            case 'ShiftRight':
                this._straightLines = true;
                break;
        }
    }
    OnKeyReleased(keycode: string): void {
        switch (keycode) {
            case 'ShiftLeft':
            case 'ShiftRight':
                this._straightLines = false;
                break;
            case 'Escape':
                this.CancelActiveMeasurement();
                break;
        }
    }

    FollowCursor(coords: THREE.Vector2) {
        // Distance
        let normal: THREE.Vector3 = new THREE.Vector3();
        let target: THREE.Vector3 | null = null,
            startPoint: THREE.Vector3 | null = null;

        // Get current normal and target
        let request: any = this._picker.requestRaycast(coords, {
            layer: ViewerLayersBit.MODELS,
            snapGeometry: this._snapMode,
            snappingTreshold: this.snappingDistance,
        });
        if (request || this._activeMeasurement) this._viewer.requestRenderFrame();
        if (request) {
            this._viewer.requestRenderFrame();
            if (this._snapMode === SnapGeometry.NONE) {
                normal = request.face.normal.clone();
                var normalMatrix = new THREE.Matrix3().getNormalMatrix(request.obejct.matrixWorld);
                normal = normal.clone().applyMatrix3(normalMatrix).normalize();
            } else {
                normal = request.normal;
            }
            target = request.point;
        }

        //If no measurement active we just adjust the cursor
        if (this._measurementStep === 0) {
            // mvoe cursor to raycast before exit
            if (normal) this._cursor.setDirection(normal);
            if (target) this._cursor.position.copy(target);
            return;
        } else {
            // We need to get the start
            switch (this._measurementMode) {
                case MeasurementMode.DISTANCE:
                    startPoint = this._startPos;
                    break;
                case MeasurementMode.ANGLE:
                    startPoint = this._startFace!.center;
                    break;
            }
        }

        if (!startPoint || !this._startNormal || !this._startPerpendicular) return;
        // If there were no intersects we check for a plane
        if (!target) {
            let up = this._startPerpendicular.clone().cross(this._startNormal);
            let plane = new THREE.Plane().setFromNormalAndCoplanarPoint(up, startPoint);
            let planeProjection = this._picker.requestPlaneIntersect(plane, coords);
            if (planeProjection) target = planeProjection;
            else return;
        }

        if (!target) return;

        // If shift is held we want straight lines only
        if (this._straightLines) {
            let dir = target!.clone().sub(startPoint!);
            let dot1 = dir.dot(this._startNormal!);
            let dot2 = dir.dot(this._startPerpendicular!);
            let normal;
            if (Math.abs(dot1) > Math.abs(dot2)) {
                normal = dot1 > 0 ? this._startNormal : this._startNormal!.clone().negate();
            } else {
                normal = dot2 > 0 ? this._startPerpendicular : this._startPerpendicular!.clone().negate();
            }
            let ray = new THREE.Ray(startPoint, normal);
            ray.closestPointToPoint(startPoint, target);
        }

        if (this._measurementMode === MeasurementMode.DISTANCE) {
            this._activeMeasurement?.updatePoints(startPoint!, target, this._startNormal, this._startPerpendicular);
            this._cursorLabel!.SetText(this._viewer.formatting.format(startPoint!.distanceTo(target!)));
        } else if (this._measurementMode === MeasurementMode.ANGLE) {
            this._activeMeasurement?.updatePoints(startPoint, target, this._startNormal, normal);
            this._cursorLabel?.SetText((this._startNormal.angleTo(normal) * MathUtils.RAD2DEG).toFixed(2).toString());
        }

        // Cursor
        if (normal) this._cursor.setDirection(normal);
        this._cursor.position.copy(target);
        this._cursorLabel?.SetPosition(target);
    }

    SetEndPoint(request: any) {
        if (!this._activeMeasurement || !this._startNormal || !this._startPerpendicular || !this._startPos) return;
        //Distance
        if (this._measurementMode === MeasurementMode.DISTANCE) {
            let normal = request.normal.clone();
            let point = request.point;
            let startPoint = this._startPos;

            // If straight lines get closest point along these rays.
            if (this._straightLines) {
                let dir = point.clone().sub(startPoint);
                let dot1 = dir.dot(this._startNormal);
                let dot2 = dir.dot(this._startPerpendicular);
                if (Math.abs(dot1) > Math.abs(dot2)) {
                    normal = dot1 > 0 ? this._startNormal.clone() : this._startNormal.clone().negate();
                } else {
                    normal = dot2 > 0 ? this._startPerpendicular.clone() : this._startPerpendicular.clone().negate();
                }
                let ray = new THREE.Ray(startPoint, normal);
                ray.closestPointToPoint(point, point);
            }

            // Setting up the final line
            this._activeMeasurement.updatePoints(startPoint.clone(), point.clone(), this._startNormal, this._startPerpendicular);
            this.measurements.push(this._activeMeasurement);
            this._SVGOverlay.createOverlaySymbol('circle',point.clone(), "red");
        }
        // Angle
        else if (this._measurementMode === MeasurementMode.ANGLE) {
            // Check if we have a face to work with
            let face = request.face as faceInfo;
            if (!face) return;
            this._activeMeasurement.updatePoints(this._startPos.clone(), face.center, this._startNormal, face.normal);
            this.measurements.push(this._activeMeasurement);
            this._SVGOverlay.createOverlaySymbol('circle',face.center.clone(), "red");
        }
    }

    ClearMeasurements() {
        if (this.measurements) {
            this._viewer.SceneManager.RemoveFromActiveScene(this.measurements.map((m) => m._mainMeasurement));
            this.measurements = [];
            this._activeMeasurement = null;
        }
        if (this._labels && this._labels.length > 0) {
            this._SVGOverlay.remove(this._labels.map((svgLabel) => svgLabel.Object));
            this._labels = [];
        }

        this._SVGOverlay.clearSymbols();
    }

    createFromData(data: any[]) {
        let self = this;
        data.forEach((measurementData) => {
            var startPos = new THREE.Vector3(measurementData.startPos.x, measurementData.startPos.y, measurementData.startPos.z);
            var startNormal = new THREE.Vector3(
                measurementData.startNormal.x,
                measurementData.startNormal.y,
                measurementData.startNormal.z
            );
            var endPos = new THREE.Vector3(measurementData.endPos.x, measurementData.endPos.y, measurementData.endPos.z);
            var endNormal = new THREE.Vector3(measurementData.endNormal.x, measurementData.endNormal.y, measurementData.endNormal.z);
            var measurement =
                measurementData.measurement === 'angle'
                    ? new AngleMeasurement(this._viewer.formatting, startPos, endPos, startNormal, endNormal)
                    : new DistanceMeasurement(this._viewer.formatting, startPos, endPos, startNormal, endNormal);
            self._labels.push(...measurement.addLabels(self._SVGOverlay));
            
            self.measurements.push(measurement);
            self._viewer.currentScene?.add(measurement._mainMeasurement);
        });
    }

    updateFormatting() {
        if (this._labels.length === 0) return;
        let self = this;

        this._SVGOverlay.remove(this._labels.map((l) => l.Object));
        this._labels = [];
        this.measurements.forEach((measurement) => {
            self._labels.push(...measurement.addLabels(self._SVGOverlay));
        });
    }
}
